import { Component, Input, Output, EventEmitter, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Notification } from '@novu/js';

// Define interfaces for events
interface ActionEvent {
  notification: Notification;
  actionType: 'primary' | 'secondary';
}

@Component({
  selector: 'app-notification-item',
  templateUrl: './notification-item.component.html',
  styleUrls: ['./notification-item.component.scss'],
  standalone: true,
  imports: [CommonModule]
})
export class NotificationItemComponent implements OnInit {
  @Input() notification!: Notification;
  @Input() clickable = false;
  @Input() loadingPrimary = false;
  @Input() loadingSecondary = false;
  
  @Output() actionClick = new EventEmitter<ActionEvent>();
  @Output() toggleReadStatus = new EventEmitter<Notification>();
  @Output() archiveNotification = new EventEmitter<Notification>();
  @Output() notificationClick = new EventEmitter<Notification>();
  
  // Mobile detection
  isMobile = false;
  
  ngOnInit(): void {
    this.checkScreenSize();
  }
  
  // Check screen size on window resize
  @HostListener('window:resize')
  onResize() {
    this.checkScreenSize();
  }
  
  // Determine if we're on a mobile device
  private checkScreenSize(): void {
    this.isMobile = window.innerWidth < 640; // Tailwind's sm breakpoint
  }
  
  /**
   * Format relative time for the notification
   */
  formatRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (seconds < 60) {
      return 'just now';
    }
    
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    }
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    }
    
    const days = Math.floor(hours / 24);
    if (days < 30) {
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    }
    
    const months = Math.floor(days / 30);
    if (months < 12) {
      return `${months} ${months === 1 ? 'month' : 'months'} ago`;
    }
    
    const years = Math.floor(months / 12);
    return `${years} ${years === 1 ? 'year' : 'years'} ago`;
  }
  
  /**
   * Check if a string is valid JSON
   */
  isJsonString(str: string): boolean {
    if (!str) return false;
    
    try {
      // First check if it starts with typical JSON patterns
      if (!str.trim().startsWith('{') && !str.trim().startsWith('[')) {
        return false;
      }
      
      // Try to parse as JSON
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  }
  
  /**
   * Format JSON string for better display
   */
  formatJson(jsonString: string): string {
    try {
      const obj = JSON.parse(jsonString);
      return JSON.stringify(obj, null, 2);
    } catch (e) {
      return jsonString;
    }
  }
  
  /**
   * Handle notification click
   */
  onClick() {
    if (this.clickable) {
      this.notificationClick.emit(this.notification);
    }
  }
  
  /**
   * Handle primary action click
   */
  onPrimaryActionClick(event: Event) {
    event.stopPropagation();
    this.actionClick.emit({ notification: this.notification, actionType: 'primary' });
  }
  
  /**
   * Handle secondary action click
   */
  onSecondaryActionClick(event: Event) {
    event.stopPropagation();
    this.actionClick.emit({ notification: this.notification, actionType: 'secondary' });
  }
  
  /**
   * Toggle read status
   */
  onToggleReadStatus(event: Event) {
    event.stopPropagation();
    this.toggleReadStatus.emit(this.notification);
  }
  
  /**
   * Archive notification
   */
  onArchive(event: Event) {
    event.stopPropagation();
    this.archiveNotification.emit(this.notification);
  }
} 